import React from 'react'
import { LanguageContext } from '../contexts'
import { Workshop, WorkshopEvent } from '../Models/workshops'
import { RegistrationMessage } from '../Models/system'
import { LanguageCheckboxes } from '../Components/LanguageCheckboxes'
import { DateSelector } from '../Components/DateSelector'
import { Dropdown } from '../Components/Dropdown'
import { DateChangeActionType, FilterAction, FilterState, IdChangeActionType } from './FilterState'
import { NullableDate } from '../utility'
import { WorkshopTypeIds } from '../constants'
import { AllLanguages } from '../language'
import { RegistrationOperation } from '../Models/participant'
import { EventList } from './EventList/EventList'
import EventViewToggle, { EventViewType } from './EventViewToggle'
import EventCalendar from './EventCalendar/EventCalendar'

import './WorkshopEventsView.scss'
import { URLS } from '../http'

export interface WorkshopEventsViewProps {
	workshop: Workshop
	filterState: FilterState
	dispatchFilterAction: (action: FilterAction) => void
	selectedWorkshopEventId?: number
	recentRegistrationMessages: { [workshopEventId: number]: RegistrationMessage }
	modal: React.ReactElement | null
	onRegisterClick: (registrationOperation: RegistrationOperation) => void
	getWorkshopEventClickHandler: (workshopEvent: WorkshopEvent) => React.MouseEventHandler
	onRegistrationMessageDismissal: (workshopEventId: number) => void
	selectedEventView: EventViewType
	onSelectEventView: (view: EventViewType) => void
	selectWorkshopEventId: (workshopEventId?: number) => void
}

export function WorkshopEventsView(props: WorkshopEventsViewProps) {

	const { localizedText, language } = React.useContext(LanguageContext)
	const { filterState } = props
	const { ranges, selections } = filterState

	function getDateChangeHandler(actionType: DateChangeActionType) {
		return function (value: NullableDate) {
			props.dispatchFilterAction({ type: actionType, date: value })
		}
	}

	function getIdChangeHandler(actionType: IdChangeActionType) {
		return function (value: number | undefined) {
			props.dispatchFilterAction({ type: actionType, Id: value })
		}
	}

	let
		tier1Dropdown: React.ReactElement | null = null,
		tier2Dropdown: React.ReactElement | null = null

	if (filterState.selections.workshopTypeId == WorkshopTypeIds['InPerson']) {
		tier1Dropdown =
			<Dropdown items={ranges.locationTier1MenuItems} value={selections.locationTier1Id} onChange={getIdChangeHandler('LocationTier1IdChange')} placeholder='' label={localizedText.County} />

		if (filterState.selections.locationTier1Id) {
			tier2Dropdown =
				<Dropdown items={ranges.locationTier2MenuItems} value={selections.locationTier2Id} onChange={getIdChangeHandler('LocationTier2IdChange')} placeholder='' label={localizedText.Municipality} />
		}
	}

	let noWorkshopsMessage: React.ReactElement | null = null
	if (props.filterState.ranges.filteredWorkshopEvents.length == 0) {
		noWorkshopsMessage = props.filterState.selections.userHasFiltered ? 
			<p className='p-1'>{localizedText.NoWorkshopsAvailableWithCurrentFilterSelections}</p> :
			<p className='p-1'>{localizedText.NoWorkshopsAvailableOfThisType} <a href={URLS.LEADERS_LIST} target='_blank'>{localizedText.ContactYourWorkshopLeaderLinkText}</a>.</p>
	}

	const eventView = props.selectedEventView == 'List' ? 
		<EventList {...Object.assign({}, props )} /> : 
		<EventCalendar {...Object.assign({}, props, { language, localizedText })} /> 

	const clickWorkshopMessage = props.filterState.ranges.filteredWorkshopEvents.length > 0 ? 
		<div className='click-workshop-prompt'>{localizedText.ClickAWorkshopForMoreInformation}</div> : null

	return (
		<div className='workshop-events'>
			<div className='columns p-1'>
				<div className='column'>
					<h3 className='title is-4'>
						{props.workshop.WorkshopName[language]}
					</h3>
				</div>
				<div className='column is-2 is-clearfix'>
					<div className='is-pulled-right'>
						<EventViewToggle selectedView={props.selectedEventView} onSelectView={props.onSelectEventView} />
					</div>
				</div>
			</div>
			
			<div className='columns filter-controls p-1'>
				<div className='column is-two-thirds'>
					<div className='description block has-text-justified'>
						{props.workshop.Description[language]}
					</div>
					<div className='block'>
						<div className='columns'>
							<div className='column'>
								<LanguageCheckboxes allLanguages={AllLanguages} availableLanguages={ranges.availableLanguages} selectedLanguages={selections.languages} setLanguages={(languages) => props.dispatchFilterAction({ type: 'SelectedLanguagesChange', languages })} />
							</div>
							<div className='column'>
								<DateSelector value={selections.startDate} onChange={getDateChangeHandler('StartDateChange')} minMax={ranges.availableDateRange} placeHolder={localizedText.SelectStartDate} label={localizedText.Start} />
							</div>
							<div className='column'>
								<DateSelector value={selections.endDate} onChange={getDateChangeHandler('EndDateChange')} minMax={ranges.availableDateRange} placeHolder={localizedText.SelectEndDate} label={localizedText.End} />
							</div>
						</div>
					</div>
				</div>
				<div className='column'>
					<Dropdown items={ranges.workshopTypeMenuItems} value={selections.workshopTypeId} onChange={getIdChangeHandler('WorkshopTypeIdChange')} placeholder={localizedText.SelectWorkshopType} label={localizedText.WorkshopType} />
					{tier1Dropdown}
					{tier2Dropdown}
					{clickWorkshopMessage}
				</div>
			</div>
			<div className='block'>
				{eventView}
				{noWorkshopsMessage}
			</div>
			{props.modal}
		</div>
	)
}